import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
	Paper,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Skeleton,
	Typography,
} from "@mui/material";
// cmps
import GroupCategory from "./group-category";
import Toast from "../Toast";
// hooks
import useSorter from "../../hooks/useSorter";
// types
import type { FilteredGroups, FilteredGroup, GroupName } from "../../types/firmware-groups";

const SKELETON_WIDTHS = [96, 64] as const;

type Props = Readonly<{
	selectedGroupName: GroupName | undefined;
	groups: FilteredGroups;
	searchFilter: string;
	onGroupClick: (groupName: GroupName | undefined) => void;
	loading: boolean;
	fetchGroups: () => void;
	showGenericErrorMsg: number | undefined;
	setShowGenericErrorMsg: (value: number | undefined) => void;
}>;

const GroupCategories = ({ selectedGroupName, groups, searchFilter, onGroupClick, loading, fetchGroups, showGenericErrorMsg, setShowGenericErrorMsg }: Props) => {
	const { t } = useTranslation();

	const sortAlphabetically = useSorter();

	const sortGroups = useCallback((groupA: FilteredGroup, groupB: FilteredGroup) => (
		sortAlphabetically(groupA.category, groupB.category)
	), [sortAlphabetically]);

	if (loading) {
		return (
			SKELETON_WIDTHS.map((skeletonWidth, index) => (
				<Paper key={index} component="section" className="sub-section">
					<List disablePadding={true}>
						<ListItem sx={{ padding: "12px 16px" }}>
							<ListItemText primary={<Skeleton width={skeletonWidth} />} />
							<ListItemIcon><Skeleton width={24} /></ListItemIcon>
						</ListItem>
					</List>
				</Paper>
			))
		);
	}

	const filteredGroups = groups
		.map((group) => ({
			...group,
			names: group.names.filter((name) => name.toLowerCase().includes(searchFilter)),
		}))
		.filter((group) => (group.names.length > 0));

	if (filteredGroups.length === 0) {
		return (
			<Typography component="h3" sx={{ marginTop: "20px", textAlign: "center" }}>
				{t("firmwareGroups.noGroup")}
			</Typography>
		);
	}

	return (
		<>
			{filteredGroups.toSorted(sortGroups).map((group) => (
				<GroupCategory
					key={group.category}
					selectedGroupName={selectedGroupName}
					group={group}
					groups={groups}
					onGroupClick={onGroupClick}
					fetchGroups={fetchGroups}
				/>
			))}
			<Toast
				autoHideDuration={6000}
				severity="error"
				open={showGenericErrorMsg}
				onClose={setShowGenericErrorMsg}
				message={t("toast.genericErrorMsg")}
			/>
		</>
	);
};

export default memo(GroupCategories);
